import React from 'react';
import { formatNumber } from '../../shared/formatting';

export type AiChartSeries = { key: string; label: string };
export type AiChartSpec = {
    type: 'bar' | 'line' | 'pie';
    title?: string;
    xKey: string;
    series: AiChartSeries[];
    data: Array<Record<string, string | number | null>>;
};

function toNumber(value: unknown): number {
    return typeof value === 'number' && Number.isFinite(value) ? value : 0;
}

function maxValue(spec: AiChartSpec): number {
    const values: number[] = [];
    spec.data.forEach((row) => {
        spec.series.forEach((series) => values.push(Math.abs(toNumber(row[series.key]))));
    });
    return Math.max(1, ...values);
}

function validSpec(spec: AiChartSpec): boolean {
    return Array.isArray(spec.data) && Array.isArray(spec.series) && spec.series.length > 0 && typeof spec.xKey === 'string' && spec.xKey !== '';
}

function BarChart({ spec }: { spec: AiChartSpec }) {
    const max = maxValue(spec);
    const primary = spec.series[0];

    return (
        <div className="ai-chart-rows">
            {spec.data.map((row, index) => {
                const raw = toNumber(row[primary.key]);
                const widthPct = Math.min(100, Math.round((Math.abs(raw) / max) * 100));

                return (
                    <div className="ai-bar-row" key={`${String(row[spec.xKey] ?? '')}-${index}`}>
                        <span className="ai-bar-label">{String(row[spec.xKey] ?? '')}</span>
                        <div className="ai-bar-track">
                            <span className={raw < 0 ? 'ai-bar ai-bar-neg' : 'ai-bar'} style={{ width: `${widthPct}%` }} />
                        </div>
                        <span className="ai-bar-value">{formatNumber(raw)}</span>
                    </div>
                );
            })}
        </div>
    );
}

function LineChart({ spec }: { spec: AiChartSpec }) {
    const primary = spec.series[0];
    if (spec.data.length < 2) {
        return <p className="muted">Nema dovoljno tocaka za line chart.</p>;
    }

    const values = spec.data.map((row) => toNumber(row[primary.key]));
    const min = Math.min(...values);
    const max = Math.max(...values);
    const range = max - min || 1;
    const points = values.map((value, index) => {
        const x = (index / Math.max(values.length - 1, 1)) * 100;
        const y = 86 - ((value - min) / range) * 72;
        return `${x},${y}`;
    }).join(' ');

    return (
        <svg className="ai-line-chart" viewBox="0 0 100 100" preserveAspectRatio="none" role="img">
            <polyline points={points} fill="none" />
        </svg>
    );
}

function PieChart({ spec }: { spec: AiChartSpec }) {
    const primary = spec.series[0];
    const total = spec.data.reduce((sum, row) => sum + Math.abs(toNumber(row[primary.key])), 0) || 1;
    const colors = ['var(--ad-acc)', '#8a681d', '#d9b979', '#5f6368', '#b7791f', '#9f7aea'];
    let cursor = 0;

    return (
        <div className="ai-pie-wrap">
            <svg className="ai-pie-chart" viewBox="0 0 42 42" role="img">
                <circle cx="21" cy="21" r="15.915" fill="transparent" stroke="#efe7d4" strokeWidth="6" />
                {spec.data.map((row, index) => {
                    const value = Math.abs(toNumber(row[primary.key]));
                    const pct = (value / total) * 100;
                    const dash = `${pct} ${100 - pct}`;
                    const segment = (
                        <circle
                            key={`seg-${index}`}
                            cx="21"
                            cy="21"
                            r="15.915"
                            fill="transparent"
                            stroke={colors[index % colors.length]}
                            strokeWidth="6"
                            strokeDasharray={dash}
                            strokeDashoffset={-cursor}
                        />
                    );
                    cursor += pct;
                    return segment;
                })}
            </svg>
            <div className="ai-pie-legend">
                {spec.data.map((row, index) => (
                    <div className="ai-pie-item" key={`legend-${index}`}>
                        <span className="ai-dot" style={{ backgroundColor: colors[index % colors.length] }} />
                        <span>{String(row[spec.xKey] ?? '')}</span>
                        <strong>{formatNumber(toNumber(row[primary.key]))}</strong>
                    </div>
                ))}
            </div>
        </div>
    );
}

export function AiChartRenderer({ charts }: { charts: AiChartSpec[] }) {
    if (!Array.isArray(charts) || charts.length === 0) {
        return null;
    }

    return (
        <div className="ai-charts">
            {charts.map((spec, index) => {
                if (!validSpec(spec)) {
                    return (
                        <article className="card ai-chart-card" key={`invalid-${index}`}>
                            <p className="muted">Graf nije valjan pa je preskocen.</p>
                        </article>
                    );
                }

                return (
                    <article className="card ai-chart-card" key={`${spec.type}-${index}`}>
                        {spec.title ? <h4>{spec.title}</h4> : null}
                        {spec.type === 'bar' ? <BarChart spec={spec} /> : null}
                        {spec.type === 'line' ? <LineChart spec={spec} /> : null}
                        {spec.type === 'pie' ? <PieChart spec={spec} /> : null}
                    </article>
                );
            })}
        </div>
    );
}
